import { createRoot } from 'react-dom/client';
import { Component, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Puzzle, PuzzleSummary } from '../shared/types';
import { PuzzleList } from './components/PuzzleList';
import { GameView } from './components/GameView';
import { ReplayView } from './components/ReplayView';
import { EditorView } from './components/EditorView';
import { AnalysisView } from './components/AnalysisView';
import { PlayersView } from './components/PlayersView';
import { PlayerProfileView } from './components/PlayerProfileView';
import { TechniquesView } from './components/TechniquesView';
import { DrillView } from './components/DrillView';
import { DrillHistoryView } from './components/DrillHistoryView';
import { DEFAULT_MODE, DrillModeId, MODES, modeById } from './drill';
import { api } from './api';
import { getPlayerToken, migrateViaIframe } from './cookie';
import { practiceShare } from './techniques';
import { revealCountFor } from './reveal';
import { RuleId } from '../shared/rules';

const migrationReady = migrateViaIframe(); // start immediately so iframe loads in parallel with app

type Route =
  | { page: 'list' }
  | { page: 'community' }
  | { page: 'tips' }
  | { page: 'drill'; mode?: DrillModeId }
  | { page: 'drillHistory'; mode?: DrillModeId }
  | { page: 'editor'; baseId?: number; practice?: RuleId; trying?: boolean }
  | { page: 'game'; puzzleId: number }
  | { page: 'replay'; entryId: number; puzzleId: number }
  | { page: 'analyze'; puzzleId: number }
  | { page: 'players' }
  | { page: 'player'; playerKey: string };

function parseRoute(pathname: string, search: string): Route {
  const player = pathname.match(/^\/players\/([^/]+)$/);
  if (player) return { page: 'player', playerKey: decodeURIComponent(player[1]) };
  if (pathname === '/players') return { page: 'players' };

  const replay = pathname.match(/^\/replay\/(\d+)\/(\d+)$/);
  if (replay) return { page: 'replay', puzzleId: Number(replay[1]), entryId: Number(replay[2]) };

  const game = pathname.match(/^\/puzzle\/(\d+)$/);
  if (game) return { page: 'game', puzzleId: Number(game[1]) };

  const analyze = pathname.match(/^\/analyze\/(\d+)$/);
  if (analyze) return { page: 'analyze', puzzleId: Number(analyze[1]) };

  // ?try=1 is the editor's "play without saving" board. The draft it plays only lives in the
  // editor's own state, so a cold load of this URL just shows the editor (which drops the flag).
  const editorParams = new URLSearchParams(search);
  const trying = editorParams.get('try') === '1' || undefined;

  const editorImport = pathname.match(/^\/editor\/(\d+)$/);
  if (editorImport) return { page: 'editor', baseId: Number(editorImport[1]), trying };
  // ?practice=R7 comes from Tips & Tricks: open the generator with that target selected.
  // Anything the generator can't actually sample for is dropped rather than trusted.
  if (pathname === '/editor') {
    const want = editorParams.get('practice') ?? '';
    const practice = practiceShare(want as RuleId) !== undefined ? (want as RuleId) : undefined;
    return { page: 'editor', practice, trying };
  }

  // /techniques was the page's first address; links to it are still out there.
  if (pathname === '/tips-and-tricks' || pathname === '/techniques') return { page: 'tips' };
  // /drill is the default version; /drill/<id> is one of the others, and /drill/history is the
  // session log. An id we don't recognise falls back to the default rather than 404ing — the
  // versions are a testing surface and their names may change.
  const history = pathname.match(/^\/drill\/history(?:\/([a-z0-9]+))?$/);
  if (history) return { page: 'drillHistory', mode: MODES.find(m => m.id === history[1])?.id };
  const drill = pathname.match(/^\/drill(?:\/([a-z0-9]+))?$/);
  if (drill) {
    const mode = MODES.find(m => m.id === drill[1])?.id;
    return { page: 'drill', mode };
  }
  if (pathname === '/community') return { page: 'community' };

  return { page: 'list' };
}

function routeToPath(route: Route): string {
  if (route.page === 'players') return '/players';
  if (route.page === 'player') return `/players/${encodeURIComponent(route.playerKey)}`;
  if (route.page === 'game') return `/puzzle/${route.puzzleId}`;
  if (route.page === 'analyze') return `/analyze/${route.puzzleId}`;
  if (route.page === 'replay') return `/replay/${route.puzzleId}/${route.entryId}`;
  if (route.page === 'community') return '/community';
  if (route.page === 'tips') return '/tips-and-tricks';
  if (route.page === 'drill') return route.mode ? `/drill/${route.mode}` : '/drill';
  if (route.page === 'drillHistory') return route.mode ? `/drill/history/${route.mode}` : '/drill/history';
  if (route.page === 'editor') {
    const q = new URLSearchParams();
    if (route.baseId === undefined && route.practice) q.set('practice', route.practice);
    if (route.trying) q.set('try', '1');
    const base = route.baseId !== undefined ? `/editor/${route.baseId}` : '/editor';
    const qs = q.toString();
    return qs ? `${base}?${qs}` : base;
  }
  return '/';
}

/** Loads a puzzle, then hands it to the analyzer (which builds the deduction graph client-side). */
function AnalysisRoute({ puzzleId, onBack, onPrev, onNext }: {
  puzzleId: number; onBack: () => void; onPrev?: () => void; onNext?: () => void;
}) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    api.getPuzzle(puzzleId).then(p => { if (live) setPuzzle(p); }).catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, [puzzleId]);
  if (error) return <p className="loading">Could not load that puzzle.</p>;
  if (!puzzle) return <p className="loading">Loading…</p>;
  return <AnalysisView key={puzzleId} puzzle={puzzle} onBack={onBack} onPrev={onPrev} onNext={onNext} />;
}

function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname, window.location.search));
  const [completions, setCompletions] = useState<Record<string, number>>({});
  const [completionsLoaded, setCompletionsLoaded] = useState(false);
  const [skips, setSkips] = useState<Set<number>>(new Set());
  // null = still loading; the lists are loaded once here and passed down to PuzzleList
  // (no per-page refetch) and used for in-category game navigation.
  const [puzzles, setPuzzles] = useState<PuzzleSummary[] | null>(null);
  const [userPuzzles, setUserPuzzles] = useState<PuzzleSummary[] | null>(null);
  const [puzzlesError, setPuzzlesError] = useState(false);
  const [userPuzzlesError, setUserPuzzlesError] = useState(false);

  // How many of our own pushes sit below the current history entry. Stamped into history.state so
  // it survives a reload, it is what lets a "← Back" return to wherever the player actually came
  // from (history.back()) instead of a fixed page, without ever stepping off the app.
  const depth = useRef(0);

  useEffect(() => {
    const state = history.state as { qdepth?: number } | null;
    if (typeof state?.qdepth === 'number') depth.current = state.qdepth;
    else history.replaceState({ qdepth: 0 }, '');

    const onPopState = (e: PopStateEvent) => {
      depth.current = (e.state as { qdepth?: number } | null)?.qdepth ?? 0;
      setRoute(parseRoute(window.location.pathname, window.location.search));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    migrationReady.then(async () => {
      const token = getPlayerToken();

      // One-time: upload any skips that were stored in localStorage before server-side skips existed
      if (!localStorage.getItem('_qb_skips_migrated')) {
        const localSkipIds: number[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith('skipped-')) {
            const id = parseInt(key.slice(8));
            if (!isNaN(id)) localSkipIds.push(id);
          }
        }
        if (localSkipIds.length > 0) {
          await Promise.all(localSkipIds.map(id => api.setSkip(id, token, true).catch(() => {})));
        }
        localStorage.setItem('_qb_skips_migrated', '1');
        for (const id of localSkipIds) localStorage.removeItem(`skipped-${id}`);
      }

      api.getPlayerState(token).then(({ completions, skips }) => {
        setCompletions(completions);
        setSkips(new Set(skips));
        setCompletionsLoaded(true);
      }).catch(() => setCompletionsLoaded(true));
    });
    api.getPuzzles('official').then(setPuzzles).catch(() => setPuzzlesError(true));
    api.getPuzzles('user').then(setUserPuzzles).catch(() => setUserPuzzlesError(true));
  }, []);

  // Reset scroll on in-app navigation only (not popstate), so the browser's
  // native back/forward scroll restoration still returns users to their spot
  // in the puzzle list. useLayoutEffect runs before paint, so the new page
  // never flashes in a scrolled position.
  const resetScroll = useRef(false);
  useLayoutEffect(() => {
    if (resetScroll.current) {
      resetScroll.current = false;
      window.scrollTo(0, 0);
    }
  }, [route]);

  function navigate(next: Route) {
    depth.current++;
    history.pushState({ qdepth: depth.current }, '', routeToPath(next));
    resetScroll.current = true;
    setRoute(next);
  }

  /** Replace the current entry (no new history step) — for correcting a URL we can't honour. */
  function replace(next: Route) {
    history.replaceState({ qdepth: depth.current }, '', routeToPath(next));
    setRoute(next);
  }

  /**
   * Step back to the page the player came from, falling back to `to` when this entry is the first
   * one of the session (a shared link, or a fresh tab) and there is nothing of ours behind it.
   */
  function goBack(to: Route) {
    if (depth.current > 0) history.back();
    else navigate(to);
  }

  function handleComplete(puzzleId: number, entryId: number) {
    setCompletions(c => ({ ...c, [String(puzzleId)]: entryId }));
  }

  function handleSkip(puzzleId: number) {
    setSkips(s => new Set([...s, puzzleId]));
    api.setSkip(puzzleId, getPlayerToken(), true).catch(() => {});
  }

  function handleResume(puzzleId: number) {
    setSkips(s => { const next = new Set(s); next.delete(puzzleId); return next; });
    api.setSkip(puzzleId, getPlayerToken(), false).catch(() => {});
  }

  if (route.page === 'list') {
    return (
      <PuzzleList
        kind="official"
        puzzles={puzzles}
        error={puzzlesError}
        completions={completions}
        skips={skips}
        onSelect={id => navigate({ page: 'game', puzzleId: id })}
        onOpenCommunity={() => navigate({ page: 'community' })}
        onOpenTips={() => navigate({ page: 'tips' })}
        onOpenDrill={() => navigate({ page: 'drill' })}
        onCreate={() => navigate({ page: 'editor' })}
        onOpenPlayers={() => navigate({ page: 'players' })}
      />
    );
  }

  if (route.page === 'community') {
    return (
      <PuzzleList
        kind="user"
        puzzles={userPuzzles}
        error={userPuzzlesError}
        completions={completions}
        skips={skips}
        onSelect={id => navigate({ page: 'game', puzzleId: id })}
        onOpenOfficial={() => navigate({ page: 'list' })}
        onOpenTips={() => navigate({ page: 'tips' })}
        onCreate={() => navigate({ page: 'editor' })}
        onOpenPlayers={() => navigate({ page: 'players' })}
      />
    );
  }

  if (route.page === 'tips') {
    return (
      <TechniquesView
        onBack={() => navigate({ page: 'list' })}
        onPractice={rule => navigate({ page: 'editor', practice: rule })}
      />
    );
  }

  if (route.page === 'drill') {
    // Each version is its own URL, so one can be handed to someone else to try.
    return (
      <DrillView
        mode={modeById(route.mode)}
        // The default version lives at the bare /drill, not at /drill/<its id>.
        onChangeMode={id => navigate({ page: 'drill', mode: id === DEFAULT_MODE ? undefined : id })}
        onOpenHistory={() => navigate({ page: 'drillHistory', mode: route.mode })}
        // Straight to the list, not `goBack`: the version chips push history entries of their
        // own, so stepping back walked through those one at a time instead of leaving the drill.
        onBack={() => navigate({ page: 'list' })}
      />
    );
  }

  if (route.page === 'drillHistory') {
    const historyRoute = route;
    return (
      <DrillHistoryView
        mode={modeById(historyRoute.mode)}
        onChangeMode={id => navigate({ page: 'drillHistory', mode: id === DEFAULT_MODE ? undefined : id })}
        // Straight to the drill, and to the version whose history is open — not `goBack`: the
        // version chips push history entries of their own, so stepping back walked through those
        // one at a time instead of leaving the page.
        onBack={() => navigate({ page: 'drill', mode: historyRoute.mode })}
      />
    );
  }

  if (route.page === 'editor') {
    // `key` stays off `trying` on purpose: the editor must survive a trip through the practice
    // board with its draft and settings intact.
    const editorRoute = route;
    return (
      <EditorView
        key={route.practice ?? 'none'}
        initialPuzzleId={route.baseId}
        initialPractice={route.practice}
        trying={route.trying ?? false}
        onTry={() => navigate({ ...editorRoute, trying: true })}
        onExitTry={() => goBack({ ...editorRoute, trying: false })}
        onTryUnavailable={() => replace({ ...editorRoute, trying: false })}
        onBack={() => goBack({ page: 'community' })}
        onSaved={id => { api.getPuzzles('user').then(setUserPuzzles).catch(() => {}); navigate({ page: 'game', puzzleId: id }); }}
      />
    );
  }

  if (route.page === 'players') {
    return (
      <PlayersView
        onBack={() => navigate({ page: 'list' })}
        onSelect={key => navigate({ page: 'player', playerKey: key })}
      />
    );
  }

  if (route.page === 'player') {
    return (
      <PlayerProfileView
        key={route.playerKey}
        playerKey={route.playerKey}
        onBack={() => navigate({ page: 'players' })}
        onPractice={rule => navigate({ page: 'editor', practice: rule })}
      />
    );
  }

  if (route.page === 'analyze') {
    // Same category-scoped ordering the game page uses, so stepping through the explorer
    // matches stepping through the puzzles themselves.
    const inUser = (userPuzzles ?? []).some(p => p.id === route.puzzleId);
    const ids = ((inUser ? userPuzzles : puzzles) ?? []).map(p => p.id);
    const i = ids.indexOf(route.puzzleId);
    const goAnalyze = (id: number) => navigate({ page: 'analyze', puzzleId: id });
    return (
      <AnalysisRoute
        puzzleId={route.puzzleId}
        onBack={() => navigate({ page: 'game', puzzleId: route.puzzleId })}
        onPrev={i > 0 ? () => goAnalyze(ids[i - 1]) : undefined}
        onNext={i >= 0 && i < ids.length - 1 ? () => goAnalyze(ids[i + 1]) : undefined}
      />
    );
  }

  if (route.page === 'game') {
    if (!completionsLoaded) return <p className="loading">Loading…</p>;
    // Keep next/prev/random within the same category as the current puzzle.
    const inUser = (userPuzzles ?? []).some(p => p.id === route.puzzleId);
    const pool = (inUser ? userPuzzles : puzzles) ?? [];
    const backPage: Route = inUser ? { page: 'community' } : { page: 'list' };
    const allIds = pool.map(p => p.id);
    const maxId = allIds.length ? Math.max(...allIds) : route.puzzleId;
    const uncompleted = new Set(allIds.filter(id => !(String(id) in completions)));
    const currentIdx = allIds.indexOf(route.puzzleId);
    const go = (id: number) => navigate({ page: 'game', puzzleId: id });
    const prevId = currentIdx > 0 ? allIds[currentIdx - 1] : undefined;
    const nextId = currentIdx >= 0 && currentIdx < allIds.length - 1 ? allIds[currentIdx + 1] : undefined;
    const randomPool = allIds.filter(id => uncompleted.has(id) && id !== route.puzzleId);
    // How far into the new-player window this board is. Counted over *other* puzzles solved, so
    // a player revisiting one of their first three finds the same board they were given the
    // first time rather than one that quietly got harder.
    const solvesElsewhere = Object.keys(completions).filter(id => id !== String(route.puzzleId)).length;
    return (
      <GameView
        key={route.puzzleId}
        puzzleId={route.puzzleId}
        revealCount={revealCountFor(solvesElsewhere)}
        completedEntryId={completions[String(route.puzzleId)]}
        isSkipped={skips.has(route.puzzleId)}
        onBack={() => navigate(backPage)}
        onWatchReplay={entryId => navigate({ page: 'replay', entryId, puzzleId: route.puzzleId })}
        onComplete={handleComplete}
        onSkip={() => handleSkip(route.puzzleId)}
        onResume={() => handleResume(route.puzzleId)}
        onOpenInEditor={() => navigate({ page: 'editor', baseId: route.puzzleId })}
        onAnalyze={() => navigate({ page: 'analyze', puzzleId: route.puzzleId })}
        isLatestPuzzle={route.puzzleId === maxId}
        onPrev={prevId !== undefined ? () => go(prevId) : undefined}
        onNext={nextId !== undefined ? () => go(nextId) : undefined}
        onRandom={randomPool.length > 0 ? () => go(randomPool[Math.floor(Math.random() * randomPool.length)]) : undefined}
      />
    );
  }

  return (
    <ReplayView
      entryId={route.entryId}
      puzzleId={route.puzzleId}
      isCompleted={String(route.puzzleId) in completions}
      isSkipped={skips.has(route.puzzleId)}
      completionsLoaded={completionsLoaded}
      onSkip={() => handleSkip(route.puzzleId)}
      onBack={() => navigate({ page: 'game', puzzleId: route.puzzleId })}
    />
  );
}

/**
 * A render error must not leave a blank page.
 *
 * The profile pages read a JSON payload with a couple of dozen numeric fields in it, and a server
 * running older code than the bundle — one process left up across a deploy is enough — serves a
 * field under its old name. React then unmounts the whole tree over one `undefined.toFixed`, and
 * what the player sees is nothing at all, with the real cause only in the console. Showing the
 * message beats showing an empty document, and the usual fix really is a reload.
 */
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) { console.error('[queens] render failed:', error); }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="crash">
        <h2>Something broke rendering this page.</h2>
        <p className="prof-dim">
          A reload usually fixes it. If it does not, the server and the page may be running
          different versions — restart the server.
        </p>
        <pre className="crash-detail">{this.state.error.message}</pre>
        <button className="btn-nav" onClick={() => location.reload()}>Reload</button>
      </div>
    );
  }
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary><App /></ErrorBoundary>,
);
